import { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchUsersByAny,
  deleteUserAsync,
  selectFetchedUsers,
} from "@/store/redux/slices/user_admin/user_admin";
import { useLoader } from "@/hooks/LoaderProvider";
import { PlusOutlined } from "@/config/antd";
import Breadcrumb from "@/components/specific/Breadcrumb";
import PageHeader from "@/components/layout/Header/Page_Header";
import Button from "@/components/common/Button";
import SearchFilterBar from "@/components/specific/Search_&_Filter";
import CustomPagination from "@/components/specific/Pagination";
import UserCard from "@/pages/user_management/components/User_Card";
import EmptyState from "@/pages/user_management/components/Empty_State";
import AddUserModal from "@/pages/user_management/components/Add_User_Modal.jsx";
import UserManagementTable from "@/pages/user_management/components/UserManagementTable.jsx";
import TrashIcon from "@/assets/icons/web_Icons/trash_red.svg";
import { useNavigate } from "react-router-dom";
import { selectUserProfile } from "@/store/redux/slices/user/user_slice";
import { getOrganizationId } from "@/utils/token_functions";
import Delete from "@/components/common/alerts/Delete";
import Success from "@/components/common/alerts/Success";

const UserManagerHome = () => {
  const dispatch = useDispatch();
  const {
    users,
    total: totalUsers,
    isLoading,
  } = useSelector((state) => state.userAdmin);
  const fetchedUsersByOrg = useSelector(selectFetchedUsers);
  const [searchValue, setSearchValue] = useState("");
  const [filterValue, setFilterValue] = useState("Today");
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const { showLoader, hideLoader } = useLoader();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const navigate = useNavigate();
  const [activeFilter, setActiveFilter] = useState("all");
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [selectedUserToDelete, setSelectedUserToDelete] = useState(null);
  const [deletedUserName, setDeletedUserName] = useState("");
  const [isSuccessModalVisible, setIsSuccessModalVisible] = useState(false);

  // get user profile
  const userProfile = useSelector(selectUserProfile);

  const getUsersToUse = useCallback(() => {
    return fetchedUsersByOrg?.length > 0 ? fetchedUsersByOrg : users;
  }, [fetchedUsersByOrg, users]);

  const fetchUserData = useCallback(async () => {
    const organizationID = getOrganizationId();

    showLoader();
    try {
      await dispatch(
        fetchUsersByAny({
          key: "organizationID",
          value: organizationID,
        })
      ).unwrap();

      // Optionally keep the original fetch if needed
      // await dispatch(fetchUsersAsync({ skip: 0, limit: pageSize })).unwrap();
    } catch (error) {
      console.error("Error fetching users:", error);
    } finally {
      hideLoader();
    }
  }, [dispatch, hideLoader, showLoader]);

  useEffect(() => {
    fetchUserData();
  }, [fetchUserData]);

  const handleSearch = (value) => {
    setSearchValue(value);
  };

  const handleRefresh = useCallback(async () => {
    // 1. Reset pagination to first page
    setCurrentPage(1);

    // 2. Clear the search term
    setSearchValue("");

    // 3. Reset the active filter to 'all'
    setActiveFilter("all");

    // 4. Reset the display text for the filter button
    setFilterValue("Today");

    // 5. Fetch fresh data
    showLoader();
    try {
      await fetchUserData();
    } catch (error) {
      console.error("Error refreshing users:", error);
    } finally {
      hideLoader();
    }
  }, [fetchUserData, showLoader, hideLoader]);

  const handleFilterChange = (value) => {
    setFilterValue(value);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const filterOptions = {
    items: [
      { key: "today", label: "Today" },
      { key: "thisWeek", label: "This Week" },
      { key: "thisMonth", label: "This Month" },
    ],
    onClick: handleFilterChange,
    className: "custom-dropdown-menu",
  };

  const moreOptionsMenu = {
    items: [
      { key: "1", label: "Export" },
      { key: "2", label: "Print" },
    ],
  };

  const handleAddNew = () => {
    setIsModalVisible(true);
  };

  const handleDelete = useCallback(
    async (userId, userName) => {
      // If called with a specific ID, it's an individual delete
      if (userId) {
        // Clear any selected users to avoid confusion with bulk delete
        setSelectedUsers([]);

        const userToDelete = getUsersToUse().find((user) => user.id === userId);
        if (userToDelete) {
          setSelectedUserToDelete({ id: userToDelete._id, name: userName });
          setIsDeleteModalVisible(true);
        } else {
          // console.error("User not found with id:", userId);
        }
      }
    },
    [getUsersToUse]
  );

  const handleBulkDelete = async () => {
    if (selectedUsers.length === 0) return;
    setIsDeleteModalVisible(false);

    showLoader();
    try {
      // Get the users being deleted for the success message and map to _id
      const usersToDelete = getUsersToUse().filter((user) =>
        selectedUsers.includes(user.id)
      );

      const userCount = usersToDelete.length;

      // If only one user, use their name, otherwise use count
      if (userCount === 1) {
        setDeletedUserName(usersToDelete[0].fullname);
      } else {
        setDeletedUserName(`${userCount} users`);
      }

      // Get MongoDB _id values for the API
      const userIdsToDelete = usersToDelete.map((user) => user._id);

      // Delete all selected users using MongoDB _id
      await Promise.all(
        userIdsToDelete.map((mongoId) =>
          dispatch(deleteUserAsync(mongoId)).unwrap()
        )
      );

      // Refresh data and reset selection
      await fetchUserData();
      setSelectedUsers([]);
      setIsSuccessModalVisible(true);
    } catch (error) {
      console.error("Delete failed:", error);
    } finally {
      hideLoader();
    }
  };

  const handleDeleteConfirm = async () => {
      setIsDeleteModalVisible(false)

    // If we have selected users for bulk delete, prioritize that over single delete
    if (selectedUsers.length > 0) {
      // Bulk delete takes precedence
      await handleBulkDelete();
    } else if (selectedUserToDelete) {
      // Single user delete - only if no bulk selection
      showLoader();
      try {
        await dispatch(deleteUserAsync(selectedUserToDelete.id)).unwrap();
        setDeletedUserName(selectedUserToDelete.name);
        await fetchUserData();
        setIsDeleteModalVisible(false);
        setIsSuccessModalVisible(true);
        setSelectedUserToDelete(null);
      } catch (error) {
        console.error("Delete failed:", error);
      } finally {
        hideLoader();
      }
    }
  };

  const handleDeleteSelected = () => {
    if (selectedUsers.length > 0) {
      setIsDeleteModalVisible(true);
    }
  };

  const handleModalClose = useCallback(() => {
    setIsModalVisible(false);
    fetchUserData();
  }, [fetchUserData]);

  const handleModify = useCallback(
    (userId) => {
      navigate(`/dashboard/users/edit/${userId}`);
    },
    [navigate]
  );

  // Transform and filter data for cards (mobile view)
  const getFilteredCardsData = useCallback(() => {
    let filtered = getUsersToUse() || [];

    // Apply search filter
    if (searchValue.trim()) {
      filtered = filtered.filter(
        (user) =>
          user.fullname?.toLowerCase().includes(searchValue.toLowerCase()) ||
          user.email?.toLowerCase().includes(searchValue.toLowerCase())
      );
    }

    // Apply status filter
    switch (activeFilter) {
      case "confirmed":
        filtered = filtered.filter((user) => user.isConfirmed);
        break;
      case "unconfirmed":
        filtered = filtered.filter((user) => !user.isConfirmed);
        break;
      default:
        break;
    }

    return filtered;
  }, [getUsersToUse, searchValue, activeFilter]);

  // Transform and filter data for table (desktop view)
  const getFilteredTableData = useCallback(() => {
    let filtered = getUsersToUse() || [];

    // Apply search filter
    if (searchValue.trim()) {
      filtered = filtered.filter(
        (user) =>
          user.name?.toLowerCase().includes(searchValue.toLowerCase()) ||
          user.email?.toLowerCase().includes(searchValue.toLowerCase())
      );
    }

    // Apply status filter
    switch (activeFilter) {
      case "confirmed":
        filtered = filtered.filter((user) => user.isConfirmed);
        break;
      case "unconfirmed":
        filtered = filtered.filter((user) => !user.isConfirmed);
        break;
      default:
        break;
    }

    return filtered;
  }, [getUsersToUse, searchValue, activeFilter]);

  // Get filtered data
  const filteredTableData = getFilteredTableData();
  const filteredCardsData = getFilteredCardsData();

  const showEmptyState = () => {
    if (isLoading) {
      return false; // Don't show empty state while loading
    }

    const hasData = getUsersToUse()?.length > 0;

    if (!hasData) {
      return (
        <EmptyState
          onAddNew={handleAddNew}
          message={
            searchValue
              ? "No users found matching your search"
              : activeFilter !== "all"
              ? `No ${activeFilter} users found`
              : "No users found"
          }
        />
      );
    }

    if (filteredCardsData.length === 0 && filteredTableData.length === 0) {
      return (
        <EmptyState
          onAddNew={handleAddNew}
          message={
            searchValue
              ? "No users found matching your search"
              : activeFilter !== "all"
              ? `No ${activeFilter} users found`
              : "No users found"
          }
        />
      );
    }

    return false;
  };

  // breadcrumb items
  const items = [{ title: "Team Administration", link: "/dashboard/users" }];

  // Define sort options based on your filter buttons
  const sortOptions = [
    { key: "all", label: "All Users" },
    { key: "confirmed", label: "Confirmed Users" },
    { key: "unconfirmed", label: "Unconfirmed Users" },
  ];

  return (
    <div className="w-full my-6">
      <Breadcrumb items={items} />
      <PageHeader title="Team Administration" />

      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-5">
          <div className="flex flex-col text-left">
            <h1 className="heading-medium text-lighterGrey">
              {totalUsers} Total Users
            </h1>
            <h1 className="text-regular text-lighterGrey">
              Total number of Users onboarded
            </h1>
          </div>
          <div className="items-center hidden gap-2 lg:flex">
            <Button
              text={`All Users (${users?.length || 0})`}
              color="primary"
              className="px-2 py-1 text-sm sm:px-3 sm:py-2 sm:text-small min-w-[80px] sm:min-w-[100px]"
              iconPosition="left"
              customBgColor={activeFilter === "all" ? "primary" : "white"}
              customTextColor={activeFilter === "all" ? "white" : "primary"}
              onClick={() => setActiveFilter("all")}
            />
            <Button
              text={`Confirmed Users (${
                users?.filter((user) => user.isConfirmed)?.length || 0
              })`}
              className="border border-primary font-normal text-sm !px-3 !py-2 h-8"
              customBgColor={activeFilter === "confirmed" ? "primary" : "white"}
              customTextColor={
                activeFilter === "confirmed" ? "white" : "primary"
              }
              onClick={() => setActiveFilter("confirmed")}
            />
            <Button
              text={`Unconfirmed Users (${
                users?.filter((user) => !user.isConfirmed)?.length || 0
              })`}
              className="border border-primary font-normal text-sm !px-3 !py-2 h-8"
              customBgColor={
                activeFilter === "unconfirmed" ? "primary" : "white"
              }
              customTextColor={
                activeFilter === "unconfirmed" ? "white" : "primary"
              }
              onClick={() => setActiveFilter("unconfirmed")}
            />
          </div>
        </div>
        <div className="items-center hidden gap-3 lg:flex">
          <Button
            text={
              selectedUsers.length != 0 ? "DELETE SELECTED" : "ADD NEW USER"
            }
            icon={
              selectedUsers.length != 0 ? (
                <img src={TrashIcon} className="w-4 h-4" />
              ) : (
                <PlusOutlined />
              )
            }
            color={selectedUsers.length != 0 ? "outline" : "primary"}
            className="border border-primary font-normal text-[11px] !px-3 !py-2 h-8 whitespace-nowrap"
            iconPosition="left"
            customBgColor={selectedUsers.length != 0 ? "" : "white"}
            customTextColor={selectedUsers.length != 0 ? "red" : "primary"}
            onClick={
              selectedUsers.length != 0 ? handleDeleteSelected : handleAddNew
            }
          />
        </div>
        <Button
          text="ADD NEW"
          icon={<PlusOutlined />}
          color="primary"
          className="px-2 py-1 text-small sm:px-3 sm:py-2 sm:text-small h-8 sm:h-10 min-w-[80px] whitespace-nowrap sm:min-w-[100px] lg:hidden"
          iconPosition="left"
          customBgColor="primary"
          customTextColor="white"
          onClick={handleAddNew}
        />
      </div>

      <SearchFilterBar
        onSearch={handleSearch}
        onRefresh={handleRefresh}
        onFilterChange={(value) => {
          setActiveFilter(value);
        }}
        filterValue={filterValue}
        filterOptions={filterOptions}
        moreOptionsMenu={moreOptionsMenu}
        placeholder="Search..."
        filterType="dropdown"
        sortOptions={sortOptions}
        searchValue={searchValue}
      />

      {/* First check if there's data being loaded or if we should show empty state */}
      {isLoading ? (
        <div className="flex justify-center items-center h-64"></div>
      ) : showEmptyState() ? (
        showEmptyState()
      ) : (
        <>
          <div className="hidden lg:block">
            <UserManagementTable
              data={filteredTableData}
              setSelectedUsers={setSelectedUsers}
              currentPage={currentPage}
              pageSize={pageSize}
              total={totalUsers || filteredTableData.length}
              onDelete={handleDelete}
              onModify={handleModify}
            />
          </div>

          <div className="grid grid-cols-1 gap-4 mt-6 md:grid-cols-2 lg:hidden">
            {filteredCardsData.map((user) => (
              <UserCard
                key={user._id}
                user={user}
                onDelete={handleDelete}
                onModify={handleModify}
              />
            ))}
          </div>

          {filteredTableData.length > 0 && (
            <div className="mt-6">
              <CustomPagination
                current={currentPage}
                total={totalUsers || filteredTableData.length}
                pageSize={pageSize}
                onChange={handlePageChange}
                className="pb-8"
                showTotal={true}
              />
            </div>
          )}
        </>
      )}

      <AddUserModal
        isVisible={isModalVisible}
        onClose={handleModalClose}
        userProfile={userProfile}
      />

      {/* Delete confirmation modal */}
      <Delete
        isVisible={isDeleteModalVisible}
        onClose={() => setIsDeleteModalVisible(false)}
        onProceed={handleDeleteConfirm}
        header="Delete User?"
        paragraph={
          selectedUsers.length > 0 && !selectedUserToDelete
            ? `Are you sure you want to delete ${selectedUsers.length} selected users? This action cannot be undone.`
            : `Are you sure you want to delete "${selectedUserToDelete?.name}"? This action cannot be undone.`
        }
        proceedButtonText="Delete"
        cancelButtonText="Cancel"
      />

      {/* Success Modal */}
      <Success
        isVisible={isSuccessModalVisible}
        onClose={() => setIsSuccessModalVisible(false)}
        header="User Deleted"
        paragraph={`"${deletedUserName}" has been successfully deleted.`}
        buttonText="Done"
        showCancelButton={false}
        onButtonClick={() => setIsSuccessModalVisible(false)}
      />
    </div>
  );
};

export default UserManagerHome;
